// src/config.js
// Read runtime config (preferred), then build-time env, then defaults.

// ---- helpers ----
const rtRaw =
    (typeof window !== 'undefined' && (window.__APP_CONFIG__ || window.APP_CONFIG)) || {}
const env = (typeof process !== 'undefined' && process.env) ? process.env : {}

const isPlaceholder = v => typeof v === 'string' && /^__.*__$/i.test(v)
const asStr = v => (v == null ? '' : String(v))

// Deep-sanitize runtime config: turn "__FOO__" placeholders into empty strings
const cleanPlaceholders = obj => {
  if (!obj || typeof obj !== 'object') return {}
  const out = {}
  for (const k of Object.keys(obj)) {
    const v = obj[k]
    out[k] = typeof v === 'string'
        ? (isPlaceholder(v) ? '' : v)
        : (v && typeof v === 'object' ? cleanPlaceholders(v) : v)
  }
  return out
}
const rt = cleanPlaceholders(rtRaw)

// Normalize base so it’s safe to concatenate paths
function normalizeBase(b, fallback = '/api') {
  if (!b || isPlaceholder(b)) return fallback
  const s = String(b).trim()
  if (/^https?:\/\//i.test(s)) return s.replace(/\/+$/, '') // absolute URL: trim trailing slashes
  return `/${s.replace(/^\/+/, '').replace(/\/+$/, '')}` // relative base: ensure single leading slash
}

// Ensures we return a path-only value like "/svc"
function ensurePath(p, fallback = '/svc') {
  const v = asStr(p) || fallback
  if (/^https?:\/\//i.test(v)) {
    try {
      const pn = new URL(v).pathname.replace(/\/+$/, '')
      return pn || fallback
    } catch (_) {}
  }
  return `/${v.replace(/^\/+/, '').replace(/\/+$/, '')}`
}

// ---- API/URLs ----
export const API_BASE = normalizeBase(rt.API_BASE || env.VUE_APP_API_BASE_PATH || '/api')
export const BASE_URL = asStr(rt.BASE_URL || env.VUE_APP_BASE_URL || '/')

export const API_CLIENT_ID = asStr(rt.API_CLIENT_ID || env.VUE_APP_API_CLIENT_ID || '')

// Always return a same-origin path for the token endpoint
export const TOKEN_ENDPOINT = (() => {
  // If API_BASE is absolute, take only its pathname; otherwise use as-is
  const basePath = /^https?:\/\//i.test(API_BASE) ? new URL(API_BASE).pathname : API_BASE
  // normalize: no trailing slash, ensure leading slash, then append /token
  const clean = `/${String(basePath).replace(/^\/+/, '').replace(/\/+$/, '')}`
  return `${clean}/token` // e.g. "/api/token"
})()

// ---- Brokered services ----
// One prefix for all brokered services (defaults to "/svc")
export const SVC_PREFIX = ensurePath(rt.SVC_PREFIX || env.VUE_APP_SVC_PREFIX || '/svc')

// ---- i18n ----
export const I18N_LOCALE = asStr(rt.I18N_LOCALE || env.VUE_APP_I18N_LOCALE || 'uk')
export const I18N_FALLBACK_LOCALE = asStr(
    rt.I18N_FALLBACK_LOCALE || env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
)

// ---- misc public keys ----
export const GA_KEY = asStr(rt.GA_KEY || env.VUE_APP_GOOGLE_ANALYTICS_KEY || '')
export const MAPS_KEY = asStr(rt.MAPS_KEY || env.VUE_APP_GOOGLE_MAPS_API_KEY || '')
export const RECAPTCHA_SITE_KEY = asStr(
    rt.RECAPTCHA_SITE_KEY || env.VUE_APP_GOOGLE_RECAPTCHA_SITE_KEY || '',
)

// ---- build meta ----
export const VERSION = asStr(rt.VERSION || env.VUE_APP_VERSION || '0.0.0')
export const BUILD_ENV = asStr(rt.BUILD_ENV || env.NODE_ENV || 'production')
export const BUILD_TIME = asStr(rt.BUILD_TIME || '')

// ---- Firebase: runtime first, then env; NEVER null ----
const envFB = {
  apiKey: asStr(env.VUE_APP_FIREBASE_API_KEY),
  authDomain: asStr(env.VUE_APP_FIREBASE_AUTH_DOMAIN),
  projectId: asStr(env.VUE_APP_FIREBASE_PROJECT_ID),
  storageBucket: asStr(env.VUE_APP_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: asStr(env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID),
  appId: asStr(env.VUE_APP_FIREBASE_APP_ID),
  measurementId: asStr(env.VUE_APP_FIREBASE_MEASUREMENT_ID),
  vapidKey: asStr(env.VUE_APP_VAPID_PUBLIC_KEY),
}

function normalizeFirebase(src = {}, fallback = {}) {
  const s = src || {}
  const f = fallback || {}
  return {
    apiKey: asStr(s.apiKey || f.apiKey),
    authDomain: asStr(s.authDomain || f.authDomain),
    projectId: asStr(s.projectId || f.projectId),
    storageBucket: asStr(s.storageBucket || f.storageBucket),
    messagingSenderId: asStr(s.messagingSenderId || f.messagingSenderId),
    appId: asStr(s.appId || f.appId),
    measurementId: asStr(s.measurementId || f.measurementId),
    vapidKey: asStr(s.vapidKey || f.vapidKey),
  }
}

export const FIREBASE = normalizeFirebase(rt.FIREBASE, envFB)

// Standalone VAPID export (kept for backwards-compat)
export const VAPID_PUBLIC_KEY =
    FIREBASE.vapidKey || asStr(rt.VAPID_PUBLIC_KEY) || asStr(env.VUE_APP_VAPID_PUBLIC_KEY)

// Tiny helper to build API URLs
export const apiUrl = (path = '') =>
    `${API_BASE}${path ? `/${String(path).replace(/^\/+/, '')}` : ''}`
